import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Edit3, Save, Trash2, Split, Loader2, AlertCircle, CheckCircle2, RefreshCw, Info,
} from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { Step1SelectItem } from './Step1SelectItem';
import { ItemsDto } from '../../../../types/items';
import {
  PlaceholderEditorState,
  PlaceholderBinRow,
  PlaceholderEditAction,
  PlaceholderEditActionType,
  PlaceholderEditRequest,
  PlaceholderEditResult,
} from '../../../../types/itemTypeChange';
import { itemTypeChangeService } from '../../../../services/itemTypeChangeService';
import { PlaceholderSplitDialog } from './PlaceholderSplitDialog';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface PlaceholderEditorProps {
  onBack?: () => void;
  initialItemNo?: string | null;
}

// Lokalt redigerbar rad med endringsflagg
interface EditableRow extends PlaceholderBinRow {
  // Endrede felt (null = ingen endring, string/Date = endret verdi)
  editedSerialNo?: string | null;
  editedBatchNo?: string | null;
  editedExpiryDate?: string | null;
  // Markør
  isDeleted?: boolean;
  splitInto?: { serialNo?: string; batchNo?: string; quantity: number; expiryDate?: string | null }[] | null;
}

export const PlaceholderEditor: React.FC<PlaceholderEditorProps> = ({ onBack, initialItemNo }) => {
  const { t } = useTranslation();
  const [selectedItem, setSelectedItem] = useState<ItemsDto | null>(null);
  const [state, setState] = useState<PlaceholderEditorState | null>(null);
  const [editableRows, setEditableRows] = useState<EditableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveResult, setSaveResult] = useState<PlaceholderEditResult | null>(null);
  const [typeFilter, setTypeFilter] = useState<number[]>([4, 5, 6]);
  const [splitDialogRow, setSplitDialogRow] = useState<EditableRow | null>(null);

  const itemType = state?.itemType ?? 0;
  const isSerial = itemType === 6;
  const isBatch = itemType === 4 || itemType === 5;
  const requiresExpiry = itemType === 4;

  // Automatisk last ved initialItemNo
  useEffect(() => {
    if (initialItemNo) {
      loadItem(initialItemNo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialItemNo]);

  const loadItem = async (itemNo: string) => {
    try {
      setLoading(true);
      setError(null);
      setSaveResult(null);
      const data = await itemTypeChangeService.getPlaceholders(itemNo);
      setState(data);
      setEditableRows(data.rows.map((r) => ({ ...r })));
    } catch (e: any) {
      setError(e?.message || t('manager.modules.itemTypeChange.editor.loadFailed', {}, 'Kunne ikke laste data'));
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelected = async (item: ItemsDto) => {
    setSelectedItem(item);
    await loadItem(item.itemNo);
  };

  // Viser alle rader — ingen placeholder-filtrering
  const visibleRows = editableRows;

  const changedCount = useMemo(() => editableRows.filter(hasChanges).length, [editableRows]);

  function hasChanges(r: EditableRow): boolean {
    if (r.isDeleted) return true;
    if (r.splitInto && r.splitInto.length > 0) return true;
    if (r.editedSerialNo !== undefined && r.editedSerialNo !== r.serialNo) return true;
    if (r.editedBatchNo !== undefined && r.editedBatchNo !== r.batchNo) return true;
    if (r.editedExpiryDate !== undefined && r.editedExpiryDate !== (r.expiryDate?.substring(0, 10) ?? null)) return true;
    return false;
  }

  const updateCell = (id: number, field: 'serial' | 'batch' | 'expiry', value: string) => {
    setEditableRows((rows) =>
      rows.map((r) => {
        if (r.itemBinsId !== id) return r;
        if (field === 'serial') return { ...r, editedSerialNo: value };
        if (field === 'batch') return { ...r, editedBatchNo: value };
        if (field === 'expiry') return { ...r, editedExpiryDate: value || null };
        return r;
      })
    );
  };

  const toggleDelete = (id: number) => {
    setEditableRows((rows) =>
      rows.map((r) => (r.itemBinsId === id ? { ...r, isDeleted: !r.isDeleted } : r))
    );
  };

  const revertRow = (id: number) => {
    setEditableRows((rows) =>
      rows.map((r) =>
        r.itemBinsId === id
          ? {
              ...r,
              editedSerialNo: undefined,
              editedBatchNo: undefined,
              editedExpiryDate: undefined,
              isDeleted: false,
              splitInto: null,
            }
          : r
      )
    );
  };

  // Excel-paste: limer inn tab-separert tekst over flere rader
  const handlePaste = useCallback(
    (e: React.ClipboardEvent<HTMLInputElement>, startId: number, startField: 'serial' | 'batch' | 'expiry') => {
      const text = e.clipboardData.getData('text');
      // Bare aktiver multi-rad-paste hvis vi ser flere linjer
      if (!text.includes('\n') && !text.includes('\t')) return;
      e.preventDefault();
      const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.length > 0);
      if (lines.length === 0) return;

      const startIdx = visibleRows.findIndex((r) => r.itemBinsId === startId);
      if (startIdx < 0) return;

      const fieldOrder: ('serial' | 'batch' | 'expiry')[] = isSerial
        ? ['serial']
        : ['batch', 'expiry'];
      const startFieldIdx = fieldOrder.indexOf(startField);

      setEditableRows((rows) => {
        const next = [...rows];
        for (let li = 0; li < lines.length; li++) {
          const targetRow = visibleRows[startIdx + li];
          if (!targetRow) break;
          const cells = lines[li].split('\t');
          for (let ci = 0; ci < cells.length; ci++) {
            const fIdx = startFieldIdx + ci;
            if (fIdx >= fieldOrder.length) break;
            const field = fieldOrder[fIdx];
            const value = cells[ci].trim();
            const idx = next.findIndex((r) => r.itemBinsId === targetRow.itemBinsId);
            if (idx < 0) continue;
            if (field === 'serial') next[idx] = { ...next[idx], editedSerialNo: value };
            else if (field === 'batch') next[idx] = { ...next[idx], editedBatchNo: value };
            else if (field === 'expiry') {
              // Prøv å parse som dato
              const d = new Date(value);
              next[idx] = {
                ...next[idx],
                editedExpiryDate: isNaN(d.getTime()) ? value : d.toISOString().substring(0, 10),
              };
            }
          }
        }
        return next;
      });
    },
    [visibleRows, isSerial]
  );

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowIdx: number,
    field: 'serial' | 'batch' | 'expiry'
  ) => {
    if (e.key === 'Enter' || e.key === 'ArrowDown') {
      e.preventDefault();
      const next = document.querySelector<HTMLInputElement>(
        `[data-cell-row="${rowIdx + 1}"][data-cell-field="${field}"]`
      );
      next?.focus();
      next?.select();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = document.querySelector<HTMLInputElement>(
        `[data-cell-row="${rowIdx - 1}"][data-cell-field="${field}"]`
      );
      prev?.focus();
      prev?.select();
    }
  };

  const handleSplitConfirmed = (
    rowId: number,
    splits: { serialNo?: string; batchNo?: string; quantity: number; expiryDate?: string | null }[]
  ) => {
    setEditableRows((rows) =>
      rows.map((r) => (r.itemBinsId === rowId ? { ...r, splitInto: splits } : r))
    );
    setSplitDialogRow(null);
  };

  const buildActions = (): PlaceholderEditAction[] => {
    const actions: PlaceholderEditAction[] = [];
    for (const r of editableRows) {
      if (r.isDeleted) {
        actions.push({ type: PlaceholderEditActionType.Delete, itemBinsId: r.itemBinsId });
        continue;
      }
      if (r.splitInto && r.splitInto.length > 0) {
        actions.push({
          type: PlaceholderEditActionType.Split,
          itemBinsId: r.itemBinsId,
          splits: r.splitInto.map((s) => ({
            serialNo: isSerial ? s.serialNo : null,
            batchNo: isBatch ? s.batchNo : null,
            quantity: s.quantity,
            expiryDate: isBatch && s.expiryDate ? `${s.expiryDate.substring(0, 10)}T12:00:00` : null,
          })),
        });
        continue;
      }
      if (hasChanges(r)) {
        const expiryStr = r.editedExpiryDate !== undefined ? r.editedExpiryDate : r.expiryDate?.substring(0, 10) ?? null;
        actions.push({
          type: PlaceholderEditActionType.Update,
          itemBinsId: r.itemBinsId,
          serialNo: isSerial
            ? r.editedSerialNo !== undefined
              ? r.editedSerialNo
              : r.serialNo
            : null,
          batchNo: isBatch
            ? r.editedBatchNo !== undefined
              ? r.editedBatchNo
              : r.batchNo
            : null,
          expiryDate: isBatch && expiryStr ? `${expiryStr}T12:00:00` : null,
        });
      }
    }
    return actions;
  };

  const handleSave = async () => {
    if (!state) return;
    const actions = buildActions();
    if (actions.length === 0) return;

    try {
      setSaving(true);
      setError(null);
      const req: PlaceholderEditRequest = { itemNo: state.itemNo, actions };
      const result = await itemTypeChangeService.savePlaceholders(req);
      setSaveResult(result);
      // Last på nytt for å vise oppdatert state
      await loadItem(state.itemNo);
    } catch (e: any) {
      setError(e?.message || t('manager.modules.itemTypeChange.editor.saveFailed', {}, 'Lagring feilet'));
    } finally {
      setSaving(false);
    }
  };

  // ────────────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-full p-4 sm:p-6">
      <div className="max-w-7xl mx-auto">
        {onBack && (
          <div className="mb-4">
            <BackButton onClick={onBack} label={t('common.buttons.back', {}, 'Tilbake')} />
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="bg-red-100 rounded-full p-3 flex-shrink-0">
              <Edit3 className="h-6 w-6 text-red-600" />
            </div>
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('manager.modules.itemTypeChange.editor.title', {}, 'Rediger serie-/batchnummer')}</h1>
              <p className="text-sm text-gray-600 mt-1">
                {t('manager.modules.itemTypeChange.editor.subtitle', {}, 'Masseredigering av serie- og batchnummer per lokasjon for én vare. Paste fra Excel, splitt rader, slett enheter. Auto-genererte verdier (MIG-* / LEGACY-*) markeres med ●.')}
              </p>
            </div>
          </div>

          {!state && !loading && (
            <>
              {/* ItemType-filter */}
              <div className="bg-gray-50 rounded-md p-3 mb-4">
                <div className="text-xs font-semibold text-gray-700 mb-2">{t('manager.modules.itemTypeChange.editor.showItemsWithType', {}, 'Vis varer med varetype:')}</div>
                <div className="flex flex-wrap gap-3">
                  {[
                    { value: 4, label: t('manager.modules.itemTypeChange.editor.typeBatchExpiry', {}, 'Batch m/holdbarhet') },
                    { value: 5, label: t('manager.modules.itemTypeChange.editor.typeBatchTracked', {}, 'Batch lagerstyrt') },
                    { value: 6, label: t('manager.modules.itemTypeChange.editor.typeSerialTracked', {}, 'Serienr. lagerstyrt') },
                  ].map((opt) => {
                    const checked = typeFilter.includes(opt.value);
                    return (
                      <label key={opt.value} className="inline-flex items-center gap-1.5 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) =>
                            setTypeFilter((prev) =>
                              e.target.checked
                                ? [...prev, opt.value]
                                : prev.filter((v) => v !== opt.value)
                            )
                          }
                        />
                        <span className="text-gray-700">
                          {opt.value}: {opt.label}
                        </span>
                      </label>
                    );
                  })}
                </div>
                {typeFilter.length === 0 && (
                  <p className="text-xs text-amber-700 mt-2">
                    {t('manager.modules.itemTypeChange.editor.selectAtLeastOneType', {}, 'Velg minst én varetype for å søke.')}
                  </p>
                )}
              </div>
              <Step1SelectItem
                onItemSelected={handleItemSelected}
                itemTypeFilter={typeFilter}
                title={t('manager.modules.itemTypeChange.editor.selectItemTitle', {}, 'Velg vare')}
                subtitle={t('manager.modules.itemTypeChange.editor.selectItemSubtitle', {}, 'Kun lagerstyrte varer (4/5/6) er relevante for denne editoren. Minst 2 tegn.')}
              />
            </>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-red-500" />
            </div>
          )}

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 p-3 rounded-md mb-4">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700 flex-1">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-xs text-red-600 hover:text-red-800"
              >
                {t('common.buttons.close', {}, 'Lukk')}
              </button>
            </div>
          )}

          {saveResult?.success && (
            <div className="flex items-start gap-2 bg-green-50 border border-green-200 p-3 rounded-md mb-4">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-green-700">{saveResult.message}</p>
            </div>
          )}

          {state && (
            <>
              {/* Toppbar */}
              <div className="flex items-center justify-between flex-wrap gap-3 border-b border-gray-200 pb-3 mb-4">
                <div className="text-sm">
                  <div className="font-semibold text-gray-900">
                    {state.itemNo} — {state.description || '-'}
                  </div>
                  <div className="text-gray-500 mt-0.5">
                    {t('manager.modules.itemTypeChange.editor.rowsCount', { '0': editableRows.length }, `${editableRows.length} rader`)} ·{' '}
                    {state.placeholderCount > 0 && (
                      <>
                        <span className="text-amber-700">{t('manager.modules.itemTypeChange.editor.autoGenCount', { '0': state.placeholderCount }, `${state.placeholderCount} auto-generert`)}</span> ·{' '}
                      </>
                    )}
                    <span className="text-red-600 font-medium">{t('manager.modules.itemTypeChange.editor.changedCount', { '0': changedCount }, `${changedCount} endret`)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => state && loadItem(state.itemNo)}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
                    disabled={loading}
                  >
                    <RefreshCw className="h-3 w-3" />
                    {t('manager.modules.itemTypeChange.editor.reload', {}, 'Last på nytt')}
                  </button>
                </div>
              </div>

              {/* Tips */}
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 p-2 rounded text-xs text-blue-800 mb-3">
                <Info className="h-3.5 w-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>{t('manager.modules.itemTypeChange.editor.tipsLabel', {}, 'Tips:')}</strong> {t('manager.modules.itemTypeChange.editor.tipsText', {}, 'Enter/pil ned for å hoppe til neste rad. Paste tab-separert tekst (fra Excel) i én celle for å fylle flere rader samtidig. ● markerer auto-genererte verdier (MIG-*/LEGACY-*).')}
                  {isSerial && ` ${t('manager.modules.itemTypeChange.editor.tipsSerial', {}, 'Serienumre må være unike per vare.')}`}
                  {isBatch && ` ${t('manager.modules.itemTypeChange.editor.tipsBatch', {}, 'Batch-nummer må være unikt per lokasjon.')}`}
                </div>
              </div>

              {/* Tabell */}
              {visibleRows.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-8">{t('manager.modules.itemTypeChange.editor.noRowsFound', {}, 'Ingen ItemBins-rader funnet for denne varen.')}</p>
              ) : (
                <div className="overflow-x-auto border border-gray-200 rounded">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.general.bin', {}, 'Lokasjon')}</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('warehouse.general.warehouse', {}, 'Lager')}</th>
                        <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('common.fields.quantity', {}, 'Antall')}</th>
                        {isSerial && (
                          <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.itemTypeChange.editor.colSerialNo', {}, 'Serienummer')}</th>
                        )}
                        {isBatch && (
                          <>
                            <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.itemTypeChange.editor.colBatch', {}, 'Batch')}</th>
                            {requiresExpiry && (
                              <th className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('manager.modules.itemTypeChange.editor.colExpiry', {}, 'Utløpsdato')}</th>
                            )}
                          </>
                        )}
                        <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase">{t('manager.modules.itemTypeChange.editor.colActions', {}, 'Handlinger')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {visibleRows.map((row, rowIdx) => {
                        const hasChange = hasChanges(row);
                        const isSplit = row.splitInto && row.splitInto.length > 0;
                        return (
                          <tr
                            key={row.itemBinsId}
                            className={`${
                              row.isDeleted
                                ? 'bg-red-50 line-through text-gray-400'
                                : isSplit
                                ? 'bg-purple-50'
                                : hasChange
                                ? 'bg-yellow-50'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-2 py-1 font-mono text-xs">
                              {row.binCode}
                              {row.isPlaceholder && (
                                <span className="ml-1 text-[10px] text-amber-600" title={t('manager.modules.itemTypeChange.editor.placeholderTooltip', {}, 'Placeholder')}>●</span>
                              )}
                            </td>
                            <td className="px-2 py-1 text-xs text-gray-600">{row.warehouseID}</td>
                            <td className="px-2 py-1 text-right text-xs">
                              {row.quantity}
                              {isSplit && (
                                <span className="ml-1 text-[10px] text-purple-700">
                                  → {t('manager.modules.itemTypeChange.editor.splitParts', { '0': row.splitInto!.length }, `${row.splitInto!.length} deler`)}
                                </span>
                              )}
                            </td>

                            {isSerial && (
                              <td className="px-2 py-1">
                                {isSplit ? (
                                  <span className="text-xs text-purple-700">
                                    {row.splitInto!.map((s) => s.serialNo).join(', ')}
                                  </span>
                                ) : (
                                  <input
                                    type="text"
                                    data-cell-row={rowIdx}
                                    data-cell-field="serial"
                                    value={row.editedSerialNo !== undefined ? row.editedSerialNo ?? '' : row.serialNo ?? ''}
                                    onChange={(e) => updateCell(row.itemBinsId, 'serial', e.target.value)}
                                    onPaste={(e) => handlePaste(e, row.itemBinsId, 'serial')}
                                    onKeyDown={(e) => handleKeyDown(e, rowIdx, 'serial')}
                                    disabled={row.isDeleted}
                                    className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
                                  />
                                )}
                              </td>
                            )}

                            {isBatch && (
                              <>
                                <td className="px-2 py-1">
                                  {isSplit ? (
                                    <span className="text-xs text-purple-700">
                                      {row.splitInto!
                                        .map((s) => `${s.batchNo}(${s.quantity})`)
                                        .join(', ')}
                                    </span>
                                  ) : (
                                    <input
                                      type="text"
                                      data-cell-row={rowIdx}
                                      data-cell-field="batch"
                                      value={
                                        row.editedBatchNo !== undefined ? row.editedBatchNo ?? '' : row.batchNo ?? ''
                                      }
                                      onChange={(e) => updateCell(row.itemBinsId, 'batch', e.target.value)}
                                      onPaste={(e) => handlePaste(e, row.itemBinsId, 'batch')}
                                      onKeyDown={(e) => handleKeyDown(e, rowIdx, 'batch')}
                                      disabled={row.isDeleted}
                                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs font-mono focus:ring-1 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
                                    />
                                  )}
                                </td>
                                {requiresExpiry && (
                                  <td className="px-2 py-1">
                                    {isSplit ? (
                                      <span className="text-xs text-purple-700">{t('manager.modules.itemTypeChange.editor.splitDates', { '0': row.splitInto!.length }, `${row.splitInto!.length} dato(er)`)}</span>
                                    ) : (
                                      <input
                                        type="date"
                                        data-cell-row={rowIdx}
                                        data-cell-field="expiry"
                                        value={
                                          row.editedExpiryDate !== undefined
                                            ? row.editedExpiryDate ?? ''
                                            : row.expiryDate?.substring(0, 10) ?? ''
                                        }
                                        onChange={(e) => updateCell(row.itemBinsId, 'expiry', e.target.value)}
                                        onPaste={(e) => handlePaste(e, row.itemBinsId, 'expiry')}
                                        onKeyDown={(e) => handleKeyDown(e, rowIdx, 'expiry')}
                                        disabled={row.isDeleted}
                                        className="px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-red-500 focus:border-red-500 disabled:bg-gray-100"
                                      />
                                    )}
                                  </td>
                                )}
                              </>
                            )}

                            <td className="px-2 py-1 text-right">
                              <div className="inline-flex items-center gap-1">
                                {(hasChange || isSplit) && !row.isDeleted && (
                                  <button
                                    onClick={() => revertRow(row.itemBinsId)}
                                    className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                                    title={t('manager.modules.itemTypeChange.editor.revertRowTooltip', {}, 'Angre endringer på raden')}
                                  >
                                    <RefreshCw className="h-3 w-3" />
                                  </button>
                                )}
                                {isBatch && !row.isDeleted && !isSplit && row.quantity > 1 && (
                                  <button
                                    onClick={() => setSplitDialogRow(row)}
                                    className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded"
                                    title={t('manager.modules.itemTypeChange.editor.splitTooltip', {}, 'Splitt til flere batcher')}
                                  >
                                    <Split className="h-3 w-3" />
                                  </button>
                                )}
                                <button
                                  onClick={() => toggleDelete(row.itemBinsId)}
                                  className={`p-1 rounded ${
                                    row.isDeleted
                                      ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                                      : 'text-red-600 hover:text-red-800 hover:bg-red-50'
                                  }`}
                                  title={row.isDeleted ? t('manager.modules.itemTypeChange.editor.undoDelete', {}, 'Angre sletting') : t('manager.modules.itemTypeChange.editor.deleteRow', {}, 'Slett rad')}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Lagre-bar */}
              <div className="flex items-center justify-between pt-4 border-t border-gray-200 mt-4">
                <button
                  onClick={() => {
                    setSelectedItem(null);
                    setState(null);
                    setEditableRows([]);
                    setSaveResult(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  {t('manager.modules.itemTypeChange.editor.selectOtherItem', {}, 'Velg annen vare')}
                </button>
                <button
                  onClick={handleSave}
                  disabled={changedCount === 0 || saving}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {t('common.buttons.save', {}, 'Lagre')} {changedCount > 0 && `(${changedCount})`}
                </button>
              </div>
            </>
          )}
        </div>

        {splitDialogRow && (
          <PlaceholderSplitDialog
            row={splitDialogRow}
            isSerial={isSerial}
            isBatch={isBatch}
            requiresExpiry={requiresExpiry}
            onCancel={() => setSplitDialogRow(null)}
            onConfirm={(splits) => handleSplitConfirmed(splitDialogRow.itemBinsId, splits)}
          />
        )}
      </div>
    </div>
  );
};
